import { api } from '@/lib/api'

export type TierKey = 'GROWTH' | 'PRO' | 'MAX'

/** Plan cadence — how often the subscription fee is charged
 *  (plans/annual-billing-and-tier-page.md). NOT the billing-cycle cadence:
 *  cycles stay monthly so per-invoice fees and usage overage keep billing. */
export type BillingIntervalKey = 'MONTHLY' | 'ANNUAL'

export interface TierUsage {
  monthUsd: number
  capUsd: number
  percent: number
  overCap: boolean
}

export interface TierVoiceMeter {
  usedMinutes: number
  includedMinutes: number
  percent: number
  overageRatePerHourUsd: number | null
  exhausted: boolean
}

export interface TierSmsMeter {
  usedMessages: number
  includedMessages: number
  percent: number
  overageRatePer100Usd: number | null
  exhausted: boolean
}

export interface TierSummary {
  tier: TierKey
  tierLabel: string
  pendingTier: TierKey | null
  monthlyFeeUsd: number
  /** The account's effective feature map from the runtime tier config —
   *  TierGate locks on `features[key] === false`. */
  features: Record<string, boolean>
  subscriptionStatus: string
  /** The account's plan cadence. The /upgrade page hides the yearly CTA for a
   *  tier the account already has on that cadence. */
  subscriptionInterval: BillingIntervalKey
  pendingSubscriptionInterval: BillingIntervalKey | null
  /** What the account pays per renewal — the annual figure on an annual plan. */
  renewalFeeUsd: number
  /** End of the prepaid year (ISO); null on monthly. */
  prepaidThrough: string | null
  cardOnFile: boolean
  /** null on paid tiers — render as "Unlimited" */
  usage: TierUsage | null
  /** Voice/SMS allowance meters; null when the tier has no allowances
   *  configured (the bundled default). Per-unit null = that unit unlimited. */
  meters: { voice: TierVoiceMeter | null; sms: TierSmsMeter | null } | null
}

/** One tier's runtime definition (price + features + inclusions) as the
 *  /upgrade surfaces render it. The sales copy derives from this payload via
 *  tierPitch (@tenfour/contracts) — see modules/billing/tier-pitch.ts. */
export interface TierCatalogEntry {
  tier: TierKey
  label: string
  monthlyFeeUsd: number
  /* The yearly figures are optional because web and api deploy separately: an
     api that predates yearly billing omits them, and arithmetic on `undefined`
     would print "$NaN" on a money page. `pricingFor` recomputes from the
     monthly price + the bundled discount when they're absent. */
  /** Percent off 12x the monthly price when billed yearly. */
  annualDiscountPercent?: number
  /** One yearly charge. */
  annualFeeUsd?: number
  /** The "$89.10 /mo, billed yearly" headline figure. */
  annualMonthlyEquivalentUsd?: number
  /** Yearly vs. 12 monthly charges. */
  annualSavingsUsd?: number
  features: Record<string, boolean>
  includedVoiceMinutes: number | null
  includedSmsMessages: number | null
  /** Monthly AI budget in USD; null = unlimited. */
  llmBudgetUsd: number | null
  voiceOverageRatePerHourUsd: number | null
  smsOverageRatePer100Usd: number | null
  /* Optional for the same deploy-skew reason as the yearly figures: an api
     that predates team chat omits them, and tierPitch claims nothing for
     undefined (never "unlimited" by accident). */
  /** Team group chats the tier may create; null = unlimited. */
  maxGroupChats?: number | null
  /** AI meeting summaries per month; null = every meeting. */
  monthlyAiMeetings?: number | null
}

export interface TierCheckoutBootstrap {
  /** SetupIntent client secret for the Payment Element; null when a card is already on file */
  clientSecret: string | null
  cardOnFile: boolean
  amountDueUsd: number
}

export function getTierSummary(accountId: string): Promise<TierSummary> {
  return api(`/accounts/${accountId}/tier-usage`)
}

export function getTierCatalog(accountId: string): Promise<{ tiers: TierCatalogEntry[] }> {
  return api(`/accounts/${accountId}/tier-catalog`)
}

// `interval` is optional server-side (defaults MONTHLY), so omitting it keeps
// the pre-annual behavior exactly.
export function createTierCheckout(
  accountId: string,
  tier: Extract<TierKey, 'PRO' | 'MAX'>,
  interval: BillingIntervalKey = 'MONTHLY',
): Promise<TierCheckoutBootstrap> {
  return api(`/accounts/${accountId}/tier/checkout`, {
    method: 'POST',
    body: JSON.stringify({ tier, interval }),
  })
}

export function confirmTierUpgrade(
  accountId: string,
  tier: Extract<TierKey, 'PRO' | 'MAX'>,
  interval: BillingIntervalKey = 'MONTHLY',
): Promise<TierSummary> {
  return api(`/accounts/${accountId}/tier/confirm`, {
    method: 'POST',
    body: JSON.stringify({ tier, interval }),
  })
}

export function scheduleTierDowngrade(
  accountId: string,
  tier: TierKey,
  interval?: BillingIntervalKey,
): Promise<TierSummary> {
  return api(`/accounts/${accountId}/tier/downgrade`, {
    method: 'POST',
    body: JSON.stringify({ tier, ...(interval ? { interval } : {}) }),
  })
}
